import type { AsyncDataOptions } from '#app'
import type { Product, ProductWith, Value } from '@scayle/storefront-nuxt'
import type { ProductRecommendationAttribute } from '~/constants/product'
import { useAsyncProductsByEan } from './useAsyncProductsByEan'
import { useFimBasket } from './useFimBasket'

type ProductRecommendationType = 'product' | 'variant'
type ProductRecommendationRefs = { product: string[]; variant: string[] }
type ProductRecommendationAttributeRefs<
  T extends ProductRecommendationAttribute,
> = { [key in T]: ProductRecommendationRefs }

const PRODUCT_WITH: ProductWith = {
  lowestPriorPrice: true,
  attributes: 'all',
  advancedAttributes: {
    withKey: ['rxCountryIndexAvailability'],
  },
  priceRange: true,
  categories: 'all',
}

type Options = AsyncDataOptions<Product[] | undefined>

// https://aboutyou.atlassian.net/wiki/spaces/AYC/pages/1394081968/FIM+Product+Data+Recommendations
export const useAsyncProductRecommendations = <
  T extends ProductRecommendationAttribute,
>(
  recommendationAttributes: T[],
  product: MaybeRefOrGetter<Product>,
  options?: Options,
) => {
  const splitRecommendation = (str: string) => {
    const [type, ...ref] = str.split('_')

    return { type: type.trim(), ref: ref.join('_').trim() } as {
      type: ProductRecommendationType
      ref: string
    }
  }

  const processRecommendationRefs = (input: string[] = []) =>
    input.reduce(
      (result, str) => {
        const { type, ref } = splitRecommendation(str)
        const group = result?.[type] ?? []

        return { ...result, [type]: [...group, ref] }
      },
      { product: [], variant: [] } as ProductRecommendationRefs,
    )

  const getUniqueRefIds = (
    refs: ProductRecommendationAttributeRefs<T>,
    type: ProductRecommendationType,
  ) => {
    if (!refs) {
      return []
    }
    const ids = recommendationAttributes.flatMap(
      (attribute) => refs?.[attribute][type],
    )
    return [...new Set(ids)]
  }

  const productAttributeRefs = computed(() =>
    recommendationAttributes.reduce((prevVal, recommendationAttribute) => {
      const attributeValues = (
        (toValue(product)?.attributes?.[recommendationAttribute]?.values ??
          []) as Value[]
      ).map((value) => value.value ?? '')
      const processedRefs = processRecommendationRefs(attributeValues)
      return {
        ...prevVal,
        [recommendationAttribute]: processedRefs,
      }
    }, {} as ProductRecommendationAttributeRefs<T>),
  )

  const variantIds = computed(() =>
    getUniqueRefIds(productAttributeRefs.value, 'variant'),
  )
  const referenceKeys = computed(() =>
    getUniqueRefIds(productAttributeRefs.value, 'product'),
  )

  const {
    data: productsByProductIds,
    pending: pendingByReferenceKey,
    execute: fetchByProductIds,
  } = useAsyncProductsByReferenceKey({
    params: {
      referenceKeys: referenceKeys.value,
      includeSellableForFree: true,
      with: PRODUCT_WITH,
    },
    options: {
      ...options,
      watch: [referenceKeys],
    },
  })
  const {
    data: productsByVariantIds,
    pending: pendingByEan,
    execute: fetchByVariantIds,
  } = useAsyncProductsByEan({
    params: {
      eans: variantIds.value,
      includeSellableForFree: true,
      with: {
        ...PRODUCT_WITH,
        variants: {
          lowestPriorPrice: true,
          attributes: {
            withKey: ['price', 'ean'],
          },
        },
      },
    },
    options: {
      ...options,
      watch: [variantIds],
    },
  })

  const basketPromise = useFimBasket()
  const { items: basketItems, fetching: fetchingBasket } = basketPromise
  const basketItemsValue = toValue(basketItems)

  const isProductRecommendation = (
    data: Partial<ProductRecommendation> | null | undefined,
  ): data is ProductRecommendation => {
    return Boolean(data?.product?.id)
  }

  const getData = (
    refs: ProductRecommendationRefs | undefined,
  ): ProductRecommendation[] => {
    if (!refs) {
      return []
    }
    const productIdRecommendations = filterBasketItems(
      refs.product || [],
      'product',
    ).map((ref) => {
      const product = productsByProductIds.value?.find(
        (product) => product?.referenceKey === ref,
      )
      return product ? ({ product } as ProductRecommendation) : undefined
    })

    const variantIdRecommendations = filterBasketItems(
      refs.variant || [],
      'variant',
    ).map((ref) => {
      const product = productsByVariantIds.value?.find((product) =>
        product.variants?.some((variant) => variant?.referenceKey === ref),
      )

      const variant = product?.variants?.find(
        (variant) => variant?.referenceKey === ref,
      )

      if (product && variant) {
        return { product, variant } as ProductRecommendation
      }

      return product ? ({ product } as ProductRecommendation) : undefined
    })

    return productIdRecommendations
      .concat(variantIdRecommendations)
      .filter(isProductRecommendation)
  }

  const filterBasketItems = (
    referenceKeys: string[],
    type: 'product' | 'variant',
  ) =>
    referenceKeys?.filter(
      (referenceKey) =>
        !basketItemsValue.some(
          (basketItem) => basketItem[type].referenceKey === referenceKey,
        ),
    ) ?? []

  const data = computed(() => {
    const result = {} as {
      [key in T]: ProductRecommendation[]
    }

    for (const attribute of recommendationAttributes) {
      result[attribute] = getData(productAttributeRefs.value?.[attribute])
    }

    return result
  })

  return extendPromise(
    basketPromise.then(() => ({})),
    {
      pending: computed(
        () =>
          pendingByReferenceKey.value ||
          pendingByEan.value ||
          fetchingBasket.value,
      ),
      execute: () => Promise.all([fetchByProductIds(), fetchByVariantIds()]),
      data,
    },
  )
}
